<template>
    <div>
        <el-dialog
            :visible.sync="showModal"
            width="800px"
            append-to-body
            :close-on-click-modal="false"
            @closed="resetForm"
        >
            <template slot="title">
                <div class="d-flex align-items-center" :style="{ gap: '8px' }">
                    <el-tag class="rounded-circle"><i class="el-icon-map-location"></i></el-tag>
                    Nova região para CD
                </div>
            </template>

            <el-form 
                :model="payload"
                ref="regionCdStoreForm" 
                label-position="top"
                status-icon
            > 
                <el-row :gutter="12">
                    <!-- Nome -->
                    <el-col :span="12">
                        <el-form-item 
                            prop="nome"
                            :rules="[
                                { required: true, message: 'A descrição é obrigatória' },
                            ]"
                        >
                            <label>Descrição</label>
                            <el-input
                                v-model="payload.nome" 
                                placeholder="Ex: Região 01"
                            />
                        </el-form-item>
                    </el-col>

                    <!-- Prazos -->
                    <el-col :span="12">
                        <el-form-item
                            prop="prazo_pagamento_id"
                            :rules="[
                                { required: true, message: 'O Prazo de pagamento é obrigatório', trigger: ['blur', 'change'] },
                            ]"
                        >
                            <label>Prazo pagamento</label>
                            <el-select 
                                v-model="payload.prazo_pagamento_id" 
                                placeholder="Selecione um prazo p/pagamanto"
                                clearable
                                filterable
                                :loading="isLoading"
                                @focus="getDeadlines"
                            >
                                <el-option
                                    v-for="(deadline, id) in deadlines"
                                    :key="id"
                                    :label="deadline"
                                    :value="Number(id)"
                                />
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <!-- Estados -->
                    <el-col :span="12">
                        <el-form-item
                            prop="estado"
                            :rules="[
                                { required: true, message: 'O Estado é obrigatório', trigger: ['blur', 'change'] },
                            ]"
                        >
                            <label>Estado</label>
                            <el-select 
                                v-model="payload.estado" 
                                placeholder="Selecione um estado"
                                clearable
                                filterable
                                :loading="isLoading"
                                @focus="getUfs"
                            >
                                <el-option
                                    v-for="uf in ufs"
                                    :key="uf.value"
                                    :label="uf.uf"
                                    :value="uf.uf"
                                />
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <!-- Cidades -->
                    <el-col :span="12">
                        <el-form-item
                            prop="cidade"
                            :rules="[
                                { required: true, message: 'A Cidade é obrigatória', trigger: ['blur', 'change'] },
                            ]"
                        >
                            <label>Cidade</label>
                            <el-select 
                                v-model="payload.cidade" 
                                placeholder="Selecione uma cidade"
                                clearable
                                filterable
                                :disabled="!payload.estado"
                            >
                                <el-option
                                    v-for="city in cities"
                                    :key="city.nome"
                                    :label="city.nome"
                                    :value="city.nome"
                                />
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <!-- Actions -->
                    <el-col>
                        <div class="d-flex align-items-center justify-content-end">
                            <el-form-item>
                                <el-button 
                                    plain
                                    :disabled="isLoading"
                                    @click="() => {
                                        toggleModal();
                                        resetForm();
                                    }"
                                >
                                    Cancelar
                                </el-button>

                                <el-button 
                                    type="primary"
                                    plain
                                    :disabled="isLoading"
                                    @click="onSubmit"
                                >
                                    <i v-if="isLoading" class="el-icon-loading"></i>
                                    <i class="fas fa-check-circle"></i>
                                    Salvar
                                </el-button>
                            </el-form-item>
                        </div>
                    </el-col>
                </el-row>
            </el-form>
        </el-dialog>
    </div>
</template>
<script>
    // Components
    import {
        Loading,
        Row,
        Col,
        Button,
        Input,
        Notification,
        Form, 
        FormItem,
        CheckboxGroup,
        CheckboxButton,
        RadioGroup,
        RadioButton,
        Tag,
        Drawer,
        Avatar,
        InputNumber,
        Switch,
        Dialog,
    } from "element-ui";

    //Api
    import { RegiaoCentroDistribuicaoApi } from "@/api/RegiaoCentroDistribuicaoApi.js";
    import { GeneralResourcesApi } from "@/api/GeneralResourcesApi";
    import { PrazoPagamentoApi } from "@/api/PrazoPagamentoApi";

    const generalResourcesApi = new GeneralResourcesApi();
    const regiaoCentroDistribuicaoApi = new RegiaoCentroDistribuicaoApi();
    const prazoPagamentoApi = new PrazoPagamentoApi();

    export default {
        data() {
            return {
                payload: {
                    nome: null,
                    responsavel_id: null,
                    cidade: null,
                    estado: null,
                    centro_distribuicao_id: null,
                    prazo_pagamento_id: null,
                },
                showModal: false,
                isLoading: false,
                ufs: [],
                cities: [],
                deadlines: [],
            }
        }, 
        methods: {
            onSubmit() {
                this.$refs.regionCdStoreForm.validate( async (valid) => {
                    if (!valid) {
                        return false
                    } 

                    this.isLoading = true;
                    
                    try {
                        await regiaoCentroDistribuicaoApi.store(this.payload)

                        Notification.success({
                            title: 'Sucesso',
                            message: 'Uma nova região foi criada!'
                        });
                    } catch (error) {
                        if (error?.response?.status == 422) {
                            if (error?.response?.data.errors) {
                                Object.values(error.response.data.errors)
                                .flat()
                                .forEach(err_message => {
                                    Notification.warning({
                                        title: 'Atenção',
                                        message: err_message
                                    });
                                });
                            }
                            
                            return
                        }

                        Notification.error({
                            title: 'Erro',
                            message: 'Não foi possível criar uma região!'
                        });
                    } finally {
                        this.isLoading = false;
                        this.toggleModal();
                        this.resetForm();
                        this.$emit('created');
                    }
                })
            },
            async getUfs() {
                try {
                    this.isLoading = true;

                    const res = await generalResourcesApi.pluckUfs();

                    this.ufs = res.data;
                } catch (error) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível obter os estados!'
                    });
                } finally {
                    this.isLoading = false;
                }
            },
            async getCities() {
                try {
                    this.isLoading = true;

                    const res = await generalResourcesApi.pluckCities(this.payload.estado);

                    this.cities = res;
                } catch (error) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível obter as cidades!'
                    });
                } finally {
                    this.isLoading = false;
                }
            },
            async getDeadlines() {
                try {
                    this.isLoading = true;

                    const res = await prazoPagamentoApi.pluck();

                    this.deadlines = res;
                } catch (error) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível obter as cidades!'
                    });
                } finally {
                    this.isLoading = false;
                }
            },
            /**Modal */
            toggleModal() {
                this.showModal = !this.showModal;
            },
            resetForm() {
                if (this.$refs.regionCdStoreForm) {
                    this.$refs.regionCdStoreForm.resetFields();
                }
            },
        },
        computed: {
            isAuthorized() {
                return this.$store.getters['user/isAnyRole']([
                    'adm',
                    'coord_comercial',
                    'gerente_regional'
                ]);
            },
        },
        watch: {
            "payload.estado": {
                async handler (_newValue, _oldValue) {
                    if(_newValue == null || _newValue == '') {
                        this.payload.cidade = null;
                        return;
                    } 

                    if(_newValue == null || _newValue == '' || _newValue == _oldValue) {
                        return;
                    }

                    await this.getCities();
                }
            },
        },
        components: {
            [Loading.name]: Loading,
            [Row.name]: Row,
            [Col.name]: Col,
            [Notification.name]: Notification,
            [Form.name]: Form,
            [FormItem.name]: FormItem,
            [Input.name]: Input,
            [Button.name]: Button,
            [CheckboxGroup.name]: CheckboxGroup,
            [CheckboxButton.name]: CheckboxButton,
            [RadioGroup.name]: RadioGroup,
            [RadioButton.name]: RadioButton,
            [Tag.name]: Tag,
            [Drawer.name]: Drawer,
            [Avatar.name]: Avatar,
            [InputNumber.name]: InputNumber,
            [Switch.name]: Switch,
            [Dialog.name]: Dialog,
        },
    }
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/_macro/regiao_cd/RegiaoCreateModal.vue